import React, { useEffect, useRef } from 'react';
import { BarChart3, GanttChart, Maximize2 } from 'lucide-react';
import { hasDeputy } from '../../services/projectRepresentatives';
import { DashboardProject, ProgressRow } from './dashboardModel';
import { GanttLegend, ProjectGantt } from './ProjectGantt';
import { ChartFullscreen } from './ChartFullscreen';
import { IconButton, Panel, PanelHeader, Tag } from './dashboardUi';

export type ProjectsView = 'gantt' | 'progress';

interface Props {
    rows: ReadonlyArray<ProgressRow>;
    view: ProjectsView;
    onViewChange: (v: ProjectsView) => void;
    setServiceOverride: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
    onOpen: (project: DashboardProject) => void;
    /** Vollbild offen. Der Aufrufer haelt es in fluechtigem Zustand — nach
     *  einem Neuladen soll das Cockpit nicht im Vollbild starten. */
    expanded: boolean;
    onToggleExpanded: () => void;
    isMobile: boolean;
    todayMs: number;
}

const ProgressList: React.FC<Pick<Props, 'rows' | 'onOpen' | 'setServiceOverride'> & { columns?: boolean }> = ({ rows, onOpen, setServiceOverride, columns = false }) => {
    if (rows.length === 0) return <div className="flex items-center justify-center h-24 text-xs text-slate-500">Keine aktiven Projekte</div>;
    // Im Vollbild ein Raster (1 → 2 → 3 Spalten); `divide-y` taugt im Raster
    // nicht, darum traegt dort jede Zeile ihre eigene Unterkante.
    return (
        <ul className={columns ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 content-start' : 'divide-y divide-slate-100'}>
            {rows.map(p => {
                const barColor = p.status === 'planning' ? 'bg-blue-400' : p.status === 'on_hold' ? 'bg-amber-400' : p.pct >= 90 ? 'bg-emerald-500' : p.pct >= 50 ? 'bg-blue-500' : 'bg-amber-400';
                return (
                    <li key={p.id} className={`px-3.5 py-2 ${columns ? 'border-b border-slate-100' : ''} ${p.status === 'on_hold' ? 'opacity-60' : ''}`}>
                        <button type="button" onClick={() => onOpen(p.project)} className="w-full text-left flex items-center justify-between gap-3 rounded-md hover:bg-slate-50 -mx-1 px-1 py-0.5">
                            <span className="flex items-center gap-2 min-w-0 flex-1">
                                {p.number && <span className="text-[11px] font-mono text-slate-500 shrink-0">{p.number}</span>}
                                <span className={`text-xs font-semibold truncate ${p.status === 'planning' ? 'text-blue-700' : p.status === 'on_hold' ? 'text-amber-700' : 'text-slate-800'}`}>{p.name}</span>
                                {p.status === 'planning' && <Tag tone="blue">Planung</Tag>}
                                {p.status === 'on_hold' && <Tag tone="amber">Ruht</Tag>}
                                {hasDeputy(p.project.data.metadata) && <Tag tone="teal">Gemeinsam</Tag>}
                            </span>
                            <span className="flex items-center gap-2 shrink-0 tabular">
                                {p.ct > 0 ? (<>
                                    <span className="text-[11px] font-semibold text-slate-600">{p.us}/{p.ct}</span>
                                    <span className={`text-[11px] font-semibold ${p.pct >= 90 ? 'text-emerald-600' : p.pct >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>{p.pct}%</span>
                                </>) : <span className="text-[11px] text-slate-400">—</span>}
                            </span>
                        </button>
                        {p.allInspServices.length > 1 && (
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                                {p.allInspServices.map(s => (
                                    <button key={s.id} type="button" onClick={() => setServiceOverride(prev => ({ ...prev, [p.id]: s.id }))}
                                        className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border transition-colors ${s.id === p.selectedServiceId ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700 hover:border-slate-300'}`}
                                    >{s.title.length > 20 ? s.title.slice(0, 20) + '…' : s.title} ({s.us}/{s.ct})</button>
                                ))}
                            </div>
                        )}
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1.5">
                            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${p.ct > 0 ? p.pct : (p.status === 'planning' ? 100 : 0)}%` }} />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

const ViewToggle: React.FC<Pick<Props, 'view' | 'onViewChange'>> = ({ view, onViewChange }) => (
    <div className="flex bg-slate-100 p-0.5 rounded-lg" role="tablist" aria-label="Ansicht">
        <button type="button" role="tab" aria-selected={view === 'gantt'} onClick={() => onViewChange('gantt')} className={`px-2 py-1 rounded-md text-[11px] font-semibold flex items-center gap-1 ${view === 'gantt' ? 'bg-white text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}><GanttChart className="w-3 h-3" />Zeitstrahl</button>
        <button type="button" role="tab" aria-selected={view === 'progress'} onClick={() => onViewChange('progress')} className={`px-2 py-1 rounded-md text-[11px] font-semibold ${view === 'progress' ? 'bg-white text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>Fortschritt</button>
    </div>
);

/**
 * „Meine Projekte" — Zeitstrahl oder Fortschrittsliste. In der Kachel auf
 * 60vh gedeckelt; der Vollbild-Knopf oeffnet das Diagramm bildschirmfuellend
 * (eigenes Overlay, echte Pixelbreite statt hochskalierter Grafik).
 */
export const ProjectsPanel: React.FC<Props> = ({ rows, view, onViewChange, setServiceOverride, onOpen, expanded, onToggleExpanded, isMobile, todayMs }) => {
    // Nach dem Schliessen bekommt der Vollbild-Knopf den Fokus zurueck.
    const triggerRef = useRef<HTMLSpanElement>(null);
    const wasExpanded = useRef(expanded);
    useEffect(() => {
        if (wasExpanded.current && !expanded) {
            triggerRef.current?.querySelector('button')?.focus({ preventScroll: true });
        }
        wasExpanded.current = expanded;
    }, [expanded]);

    return (
        <>
            <Panel>
                <PanelHeader
                    icon={<BarChart3 />}
                    title="Meine Projekte"
                    count={rows.length}
                    actions={<>
                        <ViewToggle view={view} onViewChange={onViewChange} />
                        <span ref={triggerRef} className="contents">
                            <IconButton label="Vollbild öffnen" aria-haspopup="dialog" aria-expanded={expanded} onClick={onToggleExpanded}>
                                <Maximize2 className="w-3.5 h-3.5" />
                            </IconButton>
                        </span>
                    </>}
                />
                {view === 'progress'
                    ? <div className="max-h-[60vh] overflow-y-auto min-h-0"><ProgressList rows={rows} onOpen={onOpen} setServiceOverride={setServiceOverride} /></div>
                    : <ProjectGantt rows={rows} onOpen={onOpen} setServiceOverride={setServiceOverride} isMobile={isMobile} todayMs={todayMs} />}
            </Panel>
            {expanded && (
                <ChartFullscreen
                    title="Meine Projekte"
                    icon={<BarChart3 />}
                    count={rows.length}
                    meta={view === 'gantt' ? <GanttLegend /> : undefined}
                    actions={<ViewToggle view={view} onViewChange={onViewChange} />}
                    onClose={onToggleExpanded}
                >
                    {view === 'progress'
                        ? <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-1 sm:px-2 py-1"><ProgressList rows={rows} onOpen={onOpen} setServiceOverride={setServiceOverride} columns /></div>
                        : <ProjectGantt rows={rows} onOpen={onOpen} setServiceOverride={setServiceOverride} isMobile={isMobile} todayMs={todayMs} fullscreen showLegend={false} />}
                </ChartFullscreen>
            )}
        </>
    );
};
